import _ from 'lodash'
import moment from 'moment'
import { connect } from 'react-redux'
import { Row, Col, Button } from 'antd'
import classNames from 'classnames'
import { Toast, Loader, Tip } from '@vzmi/shp_shared_compo'
import { FetchDataUtil, ExcelExport, ExcelExportHelper } from '@vzmi/component_ec_nodejs_util'
import { DEFAULT_DASHBOARD_DATE_RANGE, YESTERDAY } from '../../../constants/date'
import { NoData } from '../../Common/StatePage'
import SPBaseModule from '../../../lib/SPBaseModule'
import 'flatpickr/dist/themes/light.css'
import { SaleStatHelper, getParamsListByMonth, calendarDisableDate } from './Shared'

import PeriodComparison from './SaleStat/PeriodComparison'
import SaleAnalysis from './SaleStat/SaleAnalysis'
import SaleDailyChart from './SaleStat/SaleDailyChart'
import { getDefaultTimezoneMoment, generateFullPeriodFormat, numeralFormat } from '../../../../lib/renderUtil'
import DateHelper from '../../../lib/DateHelper'
import styles from './SaleStat.css'

/** setup locale for flatpickr */
import flatpickr from 'flatpickr'
import { Mandarin } from 'flatpickr/dist/l10n/zh.js'
import { StatsCard } from './StatsCard/StatsCard'
import { DATE_FORMAT, getUIFormatDate, getFormatData } from '../../../lib/MomentHelper'
import { getI13nModel } from './SaleStat.ga'
import safeAwait from '../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../lib/errorHelper'
import { IconExportButton } from '../../Common/ExportButton'
import withConnectedExport from '../../../containers/Common/WithConnectedExport'
import {
  formDecorator,
  Field
} from '../../Common/forms'
import { RangePickerField } from '../../Common/Form'
import Tabs from '../../Common/NewDashboard/Tabs/Tabs'
import Block from '../../Common/NewDashboard/Block/Block'
import FormRow from '../../Common/NewDashboard/FormRow/FormRow'

export const TAB = {
  SUMMARY: 'summary',
  ANALYSIS: 'analysis'
}

const TAB_MAP = {
  [TAB.SUMMARY]: '訂購總覽',
  [TAB.ANALYSIS]: '訂購組成分析'
}

flatpickr.localize(Mandarin)

const PAGE_VIEWS_API_LIMIT = 120

export const FIELD = {
  MAIN_RANGE: 'mainRange'
}
export const CONFIG = {
  form: 'PURCHASING_DAILY_FORM',
  initialValues: {
    [FIELD.MAIN_RANGE]: DEFAULT_DASHBOARD_DATE_RANGE
  }
}

export const computePeriodsParam = ({ periodStart, periodEnd }) => {
  // 資料統計區間 顯示 MTD 之資料統計區間，預設為當月 1 日至當日前一天
  // convert base moment to default timezone (+8)
  const startMoment = getDefaultTimezoneMoment(new Date(periodStart))
  const endMoment = getDefaultTimezoneMoment(new Date(periodEnd))
  const periodObject = {
    current: { start: moment(startMoment), end: endMoment }, // 資料統計區間
    previous: { start: moment(startMoment).subtract(1, 'month'), end: moment(endMoment).subtract(1, 'month') }, // 上月同期
    lastYear: { start: moment(startMoment).subtract(1, 'year'), end: moment(endMoment).subtract(1, 'year') } // 去年同期
  }
  const period = [
    generateFullPeriodFormat(periodObject.current),
    generateFullPeriodFormat(periodObject.previous),
    generateFullPeriodFormat(periodObject.lastYear)
  ]
  return period
}

export class SearchSaleStat extends SPBaseModule {
  constructor (props) {
    super(props)
    /*
      資料查詢區塊
      僅接受半年內之資料查詢
      起：兩年以前之日期不可選擇
      迄且現在是 12:00:00 以後：今天(含)以後之日期不可選擇
      迄且現在是 11:59:59 以前：前一天(含)以後之日期不可選擇
    */
    const {
      startMinDate
    } = DateHelper.getSaleStatDefaultPeriod()
    const [start, end] = DEFAULT_DASHBOARD_DATE_RANGE

    this.state = {
      data: [],
      periodStartMinDate: startMinDate,
      periodEndMaxDate: YESTERDAY,
      periodStart: getUIFormatDate(start),
      periodEnd: getUIFormatDate(end)
    }
    // update saleStat setting in redux
    const { updateSaleStatSettings } = this.props
    updateSaleStatSettings({
      periodStart: this.state.periodStart,
      periodEnd: this.state.periodEnd
    })
  }

  // 調整「起」的日期後，則「迄」跟著自動變動：
  // 「起」＋六個月 (180 天) ; 之後的日期 disable 不可選
  // 若「起」＋六個月 (180 天)  > 昨日，則為昨日
  disableDateFilter = dateMoment => {
    const {
      periodEndMaxDate,
      periodStartMinDate,
      periodStart
    } = this.state
    return dateMoment < moment(periodStartMinDate, DATE_FORMAT.UI) ||
      dateMoment >= calendarDisableDate(periodEndMaxDate, periodStart)
  }

  onCalendarChange = dateMoment => {
    const periodStart = dateMoment[0].format('YYYY/MM/DD')
    this.setState({
      periodStart
    })
  }

  componentDidMount () {
    const props = this.props
    if (!props.initialError || !props.currentMonthData) {
      const { periodStart, periodEnd } = this.state
      this.fetchData({ periodStart, periodEnd })
    }
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    if (!_.isEqual(prevState.defaultData, nextProps.defaultData)) {
      return {
        data: nextProps.defaultData
      }
    }
    return null
  }

  fetchMonthlyListingPageViews = async (params) => {
    const [error, listingPageViews] = await safeAwait(
      FetchDataUtil.fetchAllPages(params => {
        return this.props.fetchListingPageViews(params)
          .then(res => {
            if (res.error) {
              throw res
            }
            return res
          })
      }, {
        payload: params,
        selector: res => _.get(res, 'pageViews', [])
      })
    )
    if (error) {
      return Promise.reject(error)
    }

    return listingPageViews
  }

  fetchAllListingPageViews = async (params) => {
    const paramsWithLimit = {
      limit: PAGE_VIEWS_API_LIMIT,
      ...params
    }
    // API 限制日期區間最長一個月，因此跨月需要分開打
    const paramsList = getParamsListByMonth(paramsWithLimit)
    const [error, allListingPageViewsResp] = await safeAwait(
      Promise.all(
        // 查詢單一個月全部 pageView
        paramsList.map(perMonthParams => this.fetchMonthlyListingPageViews({
          ...paramsWithLimit,
          ...perMonthParams
        }))
      )
    )
    if (error) {
      return Promise.reject(error)
    }

    // 將每個月的 pageView 加總
    const allListingPageViews = _.reduce(allListingPageViewsResp, (sumPageViews, resp) => {
      sumPageViews = sumPageViews.concat(resp.data)
      return sumPageViews
    }, [])
    const pagination = _.flow(
      _.head,
      firstResp => _.get(firstResp, 'pagination')
    )(allListingPageViewsResp)
    return { data: allListingPageViews, pagination }
  }

  fetchData = async ({ periodStart, periodEnd }) => {
    const { fetchSaleStats } = this.props
    // 訂購區間比較
    const period = computePeriodsParam({ periodStart, periodEnd })

    const [error, payload] = await safeAwait(
      fetchSaleStats({ period }).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }
    const { stats } = payload
    this.setIsSuccess(true)
    this.setState({
      data: splitStatIntoPeriodChunk(stats)
    })
  }

  handleQuery = e => {
    // update saleStat setting in redux
    const { updateSaleStatSettings } = this.props
    updateSaleStatSettings({
      periodStart: this.state.periodStart,
      periodEnd: this.state.periodEnd
    })
    this.setIsLoading(true)
    // fetch saleStat info
    this.fetchData({
      periodStart: this.state.periodStart,
      periodEnd: this.state.periodEnd
    })
  }

  onMainRangeChange = (dateMoment, dateString) => {
    const [periodStart, periodEnd] = dateString
    this.setState({
      periodStart,
      periodEnd
    })
  }

  render () {
    const { fetchSaleStatsByDate, saleStat, onExportExcel } = this.props
    const {
      periodStart,
      periodEnd,
      data,
      isError,
      isLoading
    } = this.state

    const clz = classNames(styles.SearchSaleStat)
    const isLockSearchBtn = false

    const currentMonthData = _.find(
      data,
      { type: 'current' }
    )

    const mainRangeValue = [
      moment(periodStart, DATE_FORMAT.UI),
      moment(periodEnd, DATE_FORMAT.UI)
    ]

    return (
      <div className={clz}>
        <Loader loaded={!isLoading}>
          <Block>
            <FormRow
              label='日期區間'
              labelAlign='center'
            >
              <Field
                id={FIELD.MAIN_RANGE}
                key={FIELD.MAIN_RANGE}
                name={FIELD.MAIN_RANGE}
                data-role={FIELD.MAIN_RANGE}
                className={styles.mainRange}
                component={RangePickerField}
                defaultValue={mainRangeValue}
                onChange={this.onMainRangeChange}
                disabledDate={this.disableDateFilter}
                onCalendarChange={this.onCalendarChange}
              />
            </FormRow>
            <FormRow
              labelAlign='top'
              className={styles.tips}
            >
              <Tip type='info'>
                可查詢最早日期為兩年前，起迄區間最長為六個月，每日中午更新
              </Tip>
            </FormRow>
            <FormRow labelAlign='top'>
              <Button
                data-role='sale-stat-search-btn'
                disabled={isLockSearchBtn}
                className={styles.queryBtn}
                type='primary'
                size='large'
                onClick={this.handleQuery}
              >
                查詢
              </Button>
              <div className={styles.exportButton}>
                <IconExportButton
                  disabled={isLoading}
                  data-role='purchasing-daily-export-button'
                  onClick={onExportExcel}
                  label='下載整筆檔案'
                />
              </div>
            </FormRow>
          </Block>
          {isError ? this.renderIOError() : (<SaleCardList periodStat={currentMonthData} />)}
          {isError
            ? this.renderIOError()
            : (
              <SaleDailyChart
                fetchAllListingPageViews={this.fetchAllListingPageViews}
                fetchSaleStatsByDate={fetchSaleStatsByDate}
                periodStart={saleStat.periodStart}
                periodEnd={saleStat.periodEnd}
              />
            )
          }
          {isError ? this.renderIOError() : <PeriodComparison saleStatData={data} />}
        </Loader>
      </div>
    )
  }
}

const SaleCardList = ({ periodStat }) => {
  const data = _.get(periodStat, 'data', null)
  if (!data) {
    return <NoData iconType='history' />
  }
  const preloadedData = SaleStatHelper.prepareStatData(data)
  const listItems = !_.isEmpty(data)
    ? _.map(
      preloadedData,
      stat => {
        const { key, value } = stat
        const formatedVal = numeralFormat(value)
        const content = formatedVal
        return (
          <Col
            key={key}
            span={6}
          >
            <StatsCard
              title={SaleStatHelper.getTooltip(key)}
              titleContent={SaleStatHelper.getText(key)}
              content={content}
            />
          </Col>
        )
      })
    : <NoData iconType='history' />

  return (
    <Row
      data-role='saleStat-list'
      gutter={[12, 12]}
    >
      {listItems}
    </Row>
  )
}

const PERIOD_INDEX_KEY = {
  0: 'current',
  1: 'previous',
  2: 'lastYear'
}

const splitStatIntoPeriodChunk = (statArray) => {
  const periodChunkData = _.map(PERIOD_INDEX_KEY, (key, idx) => {
    const localStat = _.get(statArray, `[${idx}]`, null)
    if (!_.isEmpty(localStat)) {
      const { periodSegment } = localStat
      localStat.formatedPeriod = SaleStatHelper.formatPeriodSegment(periodSegment, true)
      localStat.type = key
      localStat.data = localStat.saleStats
      delete localStat.saleStats
      return localStat
    }
    return null
  })
  return periodChunkData
}

/** 訂購概況 */
class SaleStat extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      selectedTab: TAB.SUMMARY
    }
  }

  onTabChange = tabKey => this.setState({ selectedTab: tabKey })

  exportExcel = async () => {
    const {
      openExportModal,
      closeExportModal,
      fetchSaleStatsByDate,
      formValues
    } = this.props

    openExportModal()
    const [
      reportDateStart, reportDateEnd
    ] = [moment(formValues.mainRange[0]), moment(formValues.mainRange[1])].map(momentDate => getFormatData(momentDate, DATE_FORMAT.API))
    const fields = ['+newOrder']
    const params = { reportDateStart, reportDateEnd, fields }

    const [error, payload] = await safeAwait(
      fetchSaleStatsByDate(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    const data = _.get(payload, 'newOrder.statistics', [])
    // 訂單成立金額轉數字
    const newData = _.map(data, item => {
      return {
        ...item,
        amount: _.toNumber(item.amount)
      }
    })

    const ExportHelper = new ExcelExportHelper({
      headers: {
        reportDate: '日期',
        amount: '訂單成立金額',
        quantity: '訂單成立數'
      },
      fileName: 'purchasingbyday_{{ nowDate.format("YYYYMMDDHHmmss") }}',
      bookType: 'xlsx'
    })
    const option = {
      fileName: ExportHelper.getFileName({
        nowDate: getDefaultTimezoneMoment()
      }),
      sheets: [
        {
          name: ExportHelper.getSheetName(),
          headers: ExportHelper.getSheetHeaders(),
          data: ExportHelper.convertDataByHeaders(newData)
        }
      ]
    }
    const ExcelExporter = new ExcelExport(option)
    ExcelExporter.download({ bookType: ExportHelper.getBookType() })
    closeExportModal()
  }

  sendPageviewByAction = (action) => {
    const i13nModel = getI13nModel(action)
    this.props.sendDashboardPageview(i13nModel)
  }

  render () {
    const {
      className,
      saleStat,
      updateSaleStatSettings,
      fetchSaleStats,
      fetchSaleStatsByDate,
      fetchOrderCategoryDistribution,
      fetchListingPageViews
    } = this.props

    const { isError, selectedTab } = this.state
    const clz = classNames(styles.SaleStat, {
      [className]: !!className
    })
    return (
      <div
        className={clz}
        data-role='sale-stat'
      >
        <Tabs
          key='tabs'
          activeKey={selectedTab}
          onChange={this.onTabChange}
          items={[
            {
              key: TAB.SUMMARY,
              label: TAB_MAP[TAB.SUMMARY],
              children: (
                <SearchSaleStat
                  saleStat={saleStat}
                  initialError={isError}
                  updateSaleStatSettings={updateSaleStatSettings}
                  fetchSaleStats={fetchSaleStats}
                  fetchSaleStatsByDate={fetchSaleStatsByDate}
                  fetchListingPageViews={fetchListingPageViews}
                  onExportExcel={this.exportExcel}
                />
              )
            },
            {
              key: TAB.ANALYSIS,
              label: TAB_MAP[TAB.ANALYSIS],
              children: (
                <SaleAnalysis
                  saleStat={saleStat}
                  updateSaleStatSettings={updateSaleStatSettings}
                  fetchOrderCategoryDistribution={fetchOrderCategoryDistribution}
                  sendPageviewByAction={this.sendPageviewByAction}
                />
              )
            }
          ]}
        />
      </div>
    )
  }
}

const mapStateToProps = state => ({
  formValues: _.get(state, `form.${CONFIG.form}.values`)
})

const SearchFormWithDecorator = formDecorator(CONFIG)(SaleStat)

export default withConnectedExport(connect(mapStateToProps)(SearchFormWithDecorator))
